import { cos, sin } from "../math/trig.ts";
import type { FighterState, MatchState } from "../state.ts";
import { DT, TICK_RATE, tuning } from "../tuning.ts";
import type { SimEvent } from "./events.ts";

const c = tuning.cage;
const SIDES = c.sides;

/** Outward unit normals of the octagon walls, on the ground plane. */
export const WALL_NORMALS: { x: number; z: number }[] = [];
for (let i = 0; i < SIDES; i += 1) {
  const a = (i / SIDES) * Math.PI * 2;
  WALL_NORMALS.push({ x: sin(a), z: cos(a) });
}

/** Advances the shrink schedule. Fight-relative ticks. */
export function updateCage(state: MatchState, events: SimEvent[]): void {
  const cage = state.cage;
  const fightTick = state.tick - state.fightStartTick;
  const warningTicks = c.warningSeconds * TICK_RATE;
  cage.warning = fightTick >= cage.nextShrinkTick - warningTicks && fightTick < cage.nextShrinkTick;
  if (fightTick >= cage.nextShrinkTick) {
    cage.phase += 1;
    cage.targetR = Math.max(c.rMin, cage.targetR * c.shrinkFactor);
    cage.nextShrinkTick += c.shrinkEverySeconds * TICK_RATE;
    events.push({ type: "shrink", phase: cage.phase, targetR: cage.targetR });
  }
  if (cage.r > cage.targetR) {
    // The wall travels the whole distance of one shrink in moveOverSeconds.
    const shrinkDistance = cage.targetR / c.shrinkFactor - cage.targetR;
    const speed = shrinkDistance / c.moveOverSeconds;
    cage.r = Math.max(cage.targetR, cage.r - speed * DT);
  }
}

/** Keeps a fighter inside the octagon. Returns true when the wall pushed it. */
export function constrainToCage(state: MatchState, f: FighterState): boolean {
  const limit = state.cage.r - tuning.movement.radius;
  let pushed = false;
  // Two passes: a corner can need two walls.
  for (let pass = 0; pass < 2; pass += 1) {
    for (const n of WALL_NORMALS) {
      const d = f.pos.x * n.x + f.pos.z * n.z;
      if (d > limit) {
        const over = d - limit;
        f.pos.x -= n.x * over;
        f.pos.z -= n.z * over;
        pushed = true;
      }
    }
  }
  return pushed;
}

/** True when the fighter is within epsilon of any wall. */
export function atWall(state: MatchState, f: FighterState): boolean {
  const limit = state.cage.r - tuning.movement.radius - 0.02;
  for (const n of WALL_NORMALS) {
    if (f.pos.x * n.x + f.pos.z * n.z > limit) return true;
  }
  return false;
}
